import type { ArtDto } from '@nsb/core';
import { useLayoutEffect, useRef, useState, type ReactNode, type RefObject } from 'react';

/**
 * Where the words sit, the watercolor has thinned (guide §20.13: WCAG AA over
 * the image, whatever was painted). The title starts where the painting is
 * down to 60% (55% once its own 92% is applied: ink over even black paint
 * stays above 3:1 for large type), the line under it where it is under 48%
 * (above 4.5:1 for ink at any size), and the sentence and the cost bar sit on
 * paper. The very top is a little paler too, under the header's words.
 */
const FADE =
  'linear-gradient(to bottom, rgba(0,0,0,.55) 0%, #000 16%, #000 30%, rgba(0,0,0,.6) 44%, rgba(0,0,0,.47) 52%, rgba(0,0,0,.15) 70%, transparent 88%)';

/** Where the foot of the words over it falls in the painting, on a phone: past the 48% the line under the title needs. */
const WORDS_FOOT = 0.6;
/** The least a phone's banner is: 24rem. */
const PHONE_LEAST = 384;

/**
 * On a phone, the banner's height from where the words over it end (B26): whatever sits above the header (the
 * under-development band, a key's notice, the top strip) pushes the title down, and the painting reaches down with it,
 * so the title always sits in its thinning half. Null on a wider screen, where the banner's own height holds it.
 */
function usePhoneHeight(backdrop: RefObject<HTMLDivElement | null>, words: RefObject<HTMLElement | null> | undefined): number | null {
  const [height, setHeight] = useState<number | null>(null);
  useLayoutEffect(() => {
    const el = backdrop.current;
    const over = words?.current;
    if (!el || !over) return;
    const wide = window.matchMedia('(min-width: 640px)');
    const measure = () => {
      if (wide.matches) return setHeight(null);
      const top = (el.offsetParent ?? document.body).getBoundingClientRect().top;
      setHeight(Math.max(PHONE_LEAST, Math.round((over.getBoundingClientRect().bottom - top) / WORDS_FOOT)));
    };
    measure();
    const watch = new ResizeObserver(measure);
    watch.observe(over);
    watch.observe(document.documentElement);
    wide.addEventListener('change', measure);
    return () => {
      watch.disconnect();
      wide.removeEventListener('change', measure);
    };
  }, [backdrop, words]);
  return height;
}

/**
 * This month's watercolor of the place, full-bleed behind the top of the page
 * and the title, dissolving into the paper (design board, project homes;
 * Jason, 2026-10-03: "all the banner images should be full bleed").
 * Decorative: the words over it say everything. On a phone it reaches as far
 * down as the words over it (`over`) need.
 */
export function Backdrop({ art, over }: { art: ArtDto; over?: RefObject<HTMLElement | null> }) {
  const [loaded, setLoaded] = useState(false);
  const self = useRef<HTMLDivElement | null>(null);
  const phone = usePhoneHeight(self, over);
  const srcSet = art.widths.map((w) => `${art.url}?w=${w} ${w}w`).join(', ');
  return (
    <div
      ref={self}
      aria-hidden="true"
      className="pointer-events-none absolute inset-x-0 top-0 -z-10 h-[24rem] overflow-hidden sm:h-[38rem]"
      style={{ maskImage: FADE, WebkitMaskImage: FADE, ...(phone ? { height: phone } : {}) }}
    >
      <img
        src={`${art.url}?w=${art.widths.at(-1) ?? 1600}`}
        srcSet={srcSet}
        sizes="100vw"
        alt=""
        decoding="async"
        onLoad={() => setLoaded(true)}
        className={`h-full w-full object-cover transition-opacity duration-[400ms] ease-drafting ${loaded ? 'opacity-[.92]' : 'opacity-0'}`}
        style={{ objectPosition: 'center 30%' }}
      />
    </div>
  );
}

/**
 * A project's threshold, the same for NSB and the client (design board,
 * project homes): the place's watercolor full-bleed behind the top of the
 * page, and over its thinning lower half the title, sitting low, with what
 * the project is called by and where it is. Nothing else goes on the image:
 * the sentence and the cost bar follow on paper, and the work surfaces
 * (estimate rows, settings, the template) never show it (guide §20.4, §20.6).
 */
export function ProjectHeader({
  banner,
  kicker,
  title,
  place,
  placeMap,
  beside,
  children,
}: {
  banner: ArtDto | null;
  /** The project's code and states, as drawing labels on a scrap of paper of their own. */
  kicker?: ReactNode;
  title: string;
  place?: string | null;
  /** NSB's link from the site to Google Maps (siteHeader.ts); never on a client's header. */
  placeMap?: string | null;
  /** A small control beside the title: the client's Introduction mark. */
  beside?: ReactNode;
  children?: ReactNode;
}) {
  const words = useRef<HTMLDivElement | null>(null);
  return (
    <header className={banner ? 'pt-24 sm:pt-40' : ''}>
      {banner ? <Backdrop art={banner} over={words} /> : null}
      <div ref={words}>
        {kicker ? <div className="label mb-3 -ml-2 inline-flex flex-wrap items-center gap-2 rounded-[2px] bg-paper px-2 py-1 !text-ink">{kicker}</div> : null}
        {beside ? (
          <div className="flex items-center gap-4">
            <h1 className="text-[2.375rem] sm:text-[3rem]">{title}</h1>
            {beside}
          </div>
        ) : (
          <h1 className="text-[2.375rem] sm:text-[3rem]">{title}</h1>
        )}
        {place ? (
          <p className="mt-2 flex flex-wrap items-baseline gap-x-4 text-[0.9375rem]">
            <span>{place}</span>
            {placeMap ? (
              <a href={placeMap} target="_blank" rel="noreferrer" aria-label="Open in Google Maps" className="label hit underline decoration-1 underline-offset-[0.3em] hover:!text-ink">
                Map
              </a>
            ) : null}
          </p>
        ) : null}
      </div>
      {children}
    </header>
  );
}
